import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Flame, Users } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_b3b-bracket-book/artifacts/um5xk0v0_IMG_3414.webp";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.2 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

function MaskedLine({ index, children, className }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        custom={index}
        variants={lineVariants}
        initial="hidden"
        animate="show"
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onViewTournaments, openCount }) {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 90]);
  const yGlow = useTransform(scrollY, [0, 700], [0, -60]);

  return (
    <section className="relative overflow-hidden px-4 pb-16 pt-14 sm:px-8 sm:pt-20 lg:px-16 lg:pb-24">
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-purple-700/30 blur-[140px]"
      />
      <div className="pointer-events-none absolute right-[-120px] top-40 h-[320px] w-[320px] rounded-full bg-yellow-400/10 blur-[120px]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-purple-500/40 bg-purple-950/40 px-4 py-1.5 font-mono-b3b text-[10px] uppercase tracking-[0.3em] text-purple-300"
          >
            <Flame className="h-3.5 w-3.5 text-yellow-400" />
            3x3 Street Crown League
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-6xl uppercase leading-[0.92] tracking-wide sm:text-8xl lg:text-9xl"
          >
            <MaskedLine index={0}>Run the</MaskedLine>
            <MaskedLine index={1} className="text-yellow-400">
              Court.
            </MaskedLine>
            <MaskedLine index={2} className="text-stroke-gold">
              Own the city.
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-6 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            Monthly 3x3 Tournament. Four teams compete. One walks out legendary. Gather your squad, pick a date and register below before the bracket locks.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-view-tournaments-btn"
              onClick={onViewTournaments}
              className="group inline-flex items-center gap-2 rounded-full bg-yellow-400 px-7 py-3.5 font-heading text-sm font-bold uppercase tracking-widest text-black shadow-[0_0_32px_rgba(250,204,21,0.35)] transition-all hover:scale-105 hover:shadow-[0_0_48px_rgba(250,204,21,0.5)] active:scale-95"
            >
              View tournaments
              <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </button>
            <div className="inline-flex items-center gap-2 font-mono-b3b text-xs uppercase tracking-widest text-slate-400">
              <Users className="h-4 w-4 text-purple-400" />
              {openCount} open bracket{openCount === 1 ? "" : "s"}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:col-span-5 lg:block"
        >
          <motion.div style={{ y: yImg }} className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-purple-600/50 via-transparent to-yellow-400/40 blur-xl" />
            <img
              src={HERO_IMG}
              alt="Basketball tournament action under the lights"
              className="relative aspect-[4/5] w-full rounded-[2rem] border border-purple-500/30 object-cover"
            />
            <div className="glass-card absolute -bottom-6 -left-6 rounded-2xl px-5 py-4">
              <p className="font-display text-3xl leading-none text-yellow-400">4 TEAMS</p>
              <p className="mt-1 font-mono-b3b text-[10px] uppercase tracking-[0.25em] text-slate-400">
                per bracket
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
